// Recipe browser. The /catalog page is the home for recipe curation
// (RECIPE_FIRST_GRADE_PLAN §B2). Hits GET /api/admin/recipes with the
// filter row's values, renders a paginated table.
//
// Same URL-bound state pattern as /drafts: deep-linkable filters,
// back-button correctness, keepPreviousData for non-blanking page
// transitions.

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { AdminShell } from '@/components/AdminShell';
import {
  listRecipes,
  bulkPromoteRecipes,
  deleteRecipe,
  type ListRecipesParams,
  type ListRecipesResponse,
  type RecipeConfidence,
  type RecipeSource,
  type ReviewStatus,
} from '@/api/admin';
import { ApiError } from '@/api/client';
import { formatRelativeTime } from '@/lib/format';

const PAGE_SIZE = 50;

const CONFIDENCE_OPTIONS: Array<{ value: 'all' | RecipeConfidence; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'verified', label: 'Verified' },
];

const CATEGORY_OPTIONS: Array<{ value: '' | string; label: string }> = [
  { value: '', label: 'All categories' },
  { value: 'appetizer', label: 'Appetizer' },
  { value: 'main_course', label: 'Main course' },
  { value: 'side', label: 'Side' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'drink', label: 'Drink' },
];

const REVIEW_OPTIONS: Array<{ value: '' | ReviewStatus; label: string }> = [
  { value: '', label: 'All review states' },
  { value: 'flagged', label: 'Flagged (needs review)' },
  { value: 'pending', label: 'Pending (awaiting agents)' },
  { value: 'auto_passed', label: 'Auto-passed' },
  { value: 'human_reviewed', label: 'Human reviewed' },
];

const SOURCE_OPTIONS: Array<{ value: '' | RecipeSource; label: string }> = [
  { value: '', label: 'All sources' },
  { value: 'llm', label: 'LLM-generated' },
  { value: 'crea', label: 'CREA' },
  { value: 'recipe_import', label: 'Recipe import' },
  { value: 'import_shell', label: 'Import shell (empty)' },
  { value: 'ingredient', label: 'Single ingredient' },
  { value: 'founder', label: 'Founder' },
  { value: 'seed', label: 'Seed' },
];

const VALID_REVIEW = new Set(['pending', 'auto_passed', 'flagged', 'human_reviewed']);
const VALID_SOURCE = new Set(['crea', 'llm', 'recipe_site', 'recipe_import', 'founder', 'import_shell', 'seed', 'ingredient']);

function readParams(
  search: URLSearchParams,
): ListRecipesParams & { page: number; confidenceUi: 'all' | RecipeConfidence } {
  // Default to 'all' so the catalog page renders the full catalog on
  // landing. The original 'draft' default made sense early on when the
  // queue was the load-bearing curation surface, but now that the
  // catalog auto-verifies on creation and there are typically 0 drafts,
  // the default made the page look empty. Reviewers who specifically
  // want drafts switch via the Status filter dropdown.
  const rawConf = search.get('confidence');
  const confidenceUi = (
    rawConf === 'all' || rawConf === 'draft' || rawConf === 'verified'
      ? rawConf
      : 'all'
  ) as 'all' | RecipeConfidence;

  const category = search.get('recipe_category');
  const rawReview = search.get('review_status');
  const reviewStatus = rawReview && VALID_REVIEW.has(rawReview) ? (rawReview as ReviewStatus) : undefined;
  const rawSource = search.get('source');
  const source = rawSource && VALID_SOURCE.has(rawSource) ? (rawSource as RecipeSource) : undefined;
  const pageRaw = Number.parseInt(search.get('page') ?? '1', 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  return {
    confidence: confidenceUi === 'all' ? undefined : confidenceUi,
    confidenceUi,
    recipe_category: category ?? undefined,
    review_status: reviewStatus,
    source,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    page,
  };
}

export function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => readParams(searchParams), [searchParams]);
  const { page, confidenceUi, ...apiParams } = params;

  const query = useQuery<ListRecipesResponse, ApiError>({
    queryKey: ['admin', 'recipes', apiParams],
    queryFn: ({ signal }) => listRecipes(apiParams, signal),
    placeholderData: keepPreviousData,
  });

  function updateFilter(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function goToPage(newPage: number) {
    const next = new URLSearchParams(searchParams);
    if (newPage <= 1) next.delete('page');
    else next.set('page', String(newPage));
    setSearchParams(next, { replace: false });
  }

  const totalPages = query.data
    ? Math.max(1, Math.ceil(query.data.total / PAGE_SIZE))
    : 1;

  return (
    <AdminShell>
      <header className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Catalog</h1>
          <p className="mt-1 text-sm text-slate-500">
            {query.data
              ? `${query.data.total.toLocaleString()} ${query.data.total === 1 ? 'recipe' : 'recipes'}`
              : 'Loading…'}
          </p>
        </div>
        <Link to="/catalog/new" className="btn-primary text-sm">
          + New recipe
        </Link>
      </header>

      <Filters
        confidence={confidenceUi}
        category={searchParams.get('recipe_category') ?? ''}
        reviewStatus={searchParams.get('review_status') ?? ''}
        source={searchParams.get('source') ?? ''}
        onChange={updateFilter}
      />

      {params.review_status === 'auto_passed' && (
        <BulkPromoteBar
          source={params.source}
          total={query.data?.total ?? 0}
        />
      )}

      {query.isError && (
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      )}

      <div className="mt-4">
        <RecipesTable
          rows={query.data?.rows ?? []}
          loading={query.isLoading || query.isPlaceholderData}
          onDeleted={() => void query.refetch()}
        />
      </div>

      {query.data && totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={goToPage} />
      )}
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------

function Filters({
  confidence,
  category,
  reviewStatus,
  source,
  onChange,
}: {
  confidence: 'all' | RecipeConfidence;
  category: string;
  reviewStatus: string;
  source: string;
  onChange: (key: string, value: string | null) => void;
}) {
  return (
    <div className="card p-4 flex flex-wrap items-end gap-4">
      <Field label="Status">
        <select
          className="select"
          value={confidence}
          onChange={(e) => onChange('confidence', e.target.value || null)}
        >
          {CONFIDENCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Category">
        <select
          className="select"
          value={category}
          onChange={(e) => onChange('recipe_category', e.target.value || null)}
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Review">
        <select
          className="select"
          value={reviewStatus}
          onChange={(e) => onChange('review_status', e.target.value || null)}
        >
          {REVIEW_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Source">
        <select
          className="select"
          value={source}
          onChange={(e) => onChange('source', e.target.value || null)}
        >
          {SOURCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

/** Shown only when filtering review_status=auto_passed: one click promotes
 *  everything the agents vetted (optionally narrowed by the source filter)
 *  to verified + human_reviewed. */
function BulkPromoteBar({ source, total }: { source?: RecipeSource; total: number }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function promote() {
    if (!window.confirm(
      `Promote ${total} auto-passed ${source ? source + ' ' : ''}recipe(s) to Verified?`,
    )) return;
    setBusy(true);
    setError(null);
    try {
      const r = await bulkPromoteRecipes(source);
      setDone(r.promoted);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'recipes'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-center justify-between gap-4">
      <span>
        {done !== null
          ? `Promoted ${done} recipe(s) to Verified.`
          : `These ${total.toLocaleString()} recipes were vetted by the anomaly agents and can be bulk-promoted.`}
        {error && <span className="text-rose-700"> {error}</span>}
      </span>
      <button
        type="button"
        className="btn-primary text-xs disabled:opacity-40"
        onClick={() => void promote()}
        disabled={busy || total === 0 || done !== null}
      >
        {busy ? 'Promoting…' : `Promote all${source ? ` (${source})` : ''}`}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------

function RecipesTable({
  rows,
  loading,
  onDeleted,
}: {
  rows: ListRecipesResponse['rows'];
  loading: boolean;
  onDeleted: () => void;
}) {
  const [deletingId, setDeletingId] = useState<number | null>(null);

  async function confirmDelete(row: ListRecipesResponse['rows'][number]) {
    if (!window.confirm(
      `Delete "${row.dishFamilyNameEn} · ${row.variantName}"?\n\n` +
      'If this is the family\'s last variant, the dish family and its ' +
      'aliases are removed too. This cannot be undone.',
    )) return;
    setDeletingId(row.id);
    try {
      await deleteRecipe(row.id);
      onDeleted();
    } catch (e) {
      window.alert('Delete failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setDeletingId(null);
    }
  }

  if (rows.length === 0 && !loading) {
    return (
      <div className="card p-8 text-center text-slate-500">
        No recipes match these filters.
      </div>
    );
  }

  return (
    <div
      className={
        'card overflow-hidden transition-opacity ' +
        (loading ? 'opacity-60' : '')
      }
    >
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr className="text-left">
            <Th>Family</Th>
            <Th>Variant</Th>
            <Th>Category</Th>
            <Th className="text-right">Yield</Th>
            <Th className="text-right">Ingredients</Th>
            <Th>Updated</Th>
            <Th>Status</Th>
            <Th>Review</Th>
            <Th>Source</Th>
            <Th className="text-right">
              <span className="sr-only">Actions</span>
            </Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-slate-50">
              <Td>
                <Link
                  to={`/catalog/${row.id}`}
                  className="font-medium text-slate-900 hover:text-accent"
                >
                  {row.dishFamilyNameEn}
                </Link>
                {row.dishFamilyNameEn !== row.dishFamilyNameRo && (
                  <p className="text-xs text-slate-500">{row.dishFamilyNameRo}</p>
                )}
              </Td>
              <Td className="text-slate-700">{row.variantName}</Td>
              <Td className="text-slate-500">
                {row.recipeCategory ?? <span className="text-slate-400">—</span>}
              </Td>
              <Td className="text-right tabular-nums text-slate-500">
                {row.recipeYieldGrams} g
              </Td>
              <Td className="text-right tabular-nums text-slate-500">
                {row.ingredientsCount}
              </Td>
              <Td
                className="text-slate-500"
                title={new Date(row.updatedAt).toISOString()}
              >
                {formatRelativeTime(row.updatedAt)}
              </Td>
              <Td>
                <ConfidenceBadge confidence={row.confidence} />
              </Td>
              <Td>
                <ReviewBadge status={row.reviewStatus} openAnomalies={row.openAnomalies} />
              </Td>
              <Td className="text-slate-500 text-xs">{row.source}</Td>
              <Td className="text-right">
                <button
                  type="button"
                  className="btn-ghost text-xs text-rose-700 hover:text-rose-900 disabled:opacity-40"
                  disabled={deletingId !== null}
                  onClick={() => void confirmDelete(row)}
                  title="Delete this recipe (and its family, if it's the last variant)"
                >
                  {deletingId === row.id ? 'Deleting…' : 'Delete'}
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const REVIEW_BADGE: Record<ReviewStatus, { label: string; classes: string }> = {
  pending:        { label: 'Pending',  classes: 'bg-slate-100 text-slate-700 ring-slate-200' },
  auto_passed:    { label: 'Auto ✓',   classes: 'bg-sky-100 text-sky-900 ring-sky-200' },
  flagged:        { label: 'Flagged',  classes: 'bg-rose-100 text-rose-900 ring-rose-200' },
  human_reviewed: { label: 'Reviewed', classes: 'bg-emerald-100 text-emerald-900 ring-emerald-200' },
};

function ReviewBadge({ status, openAnomalies }: { status: ReviewStatus; openAnomalies: number }) {
  const b = REVIEW_BADGE[status];
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ' +
        b.classes
      }
    >
      {b.label}
      {openAnomalies > 0 && (
        <span className="font-semibold" title={`${openAnomalies} open finding(s)`}>
          ·{openAnomalies}
        </span>
      )}
    </span>
  );
}

function ConfidenceBadge({ confidence }: { confidence: RecipeConfidence }) {
  const classes =
    confidence === 'verified'
      ? 'bg-emerald-100 text-emerald-900 ring-emerald-200'
      : 'bg-amber-100 text-amber-900 ring-amber-200';
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ' +
        classes
      }
    >
      {confidence === 'verified' ? 'Verified' : 'Draft'}
    </span>
  );
}

function Th({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={
        'px-4 py-2 text-xs font-medium uppercase tracking-wider ' + className
      }
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = '',
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td className={'px-4 py-3 align-top ' + className} title={title}>
      {children}
    </td>
  );
}

// ---------------------------------------------------------------------------

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-end gap-2 text-sm">
      <button
        type="button"
        className="btn-outline disabled:opacity-40"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
      >
        ← Previous
      </button>
      <span className="px-3 text-slate-600 tabular-nums">
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        className="btn-outline disabled:opacity-40"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
      >
        Next →
      </button>
    </div>
  );
}

function ErrorBanner({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  return (
    <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 flex items-center justify-between gap-4">
      <span>
        Couldn't load recipes: <code className="font-mono text-xs">{error.message}</code>
      </span>
      <button type="button" className="btn-ghost text-xs" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}
